import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { Fade, Slide } from "react-awesome-reveal";

import image1 from "../images/Carrusel1.webp";
import image2 from "../images/Carrusel2.webp";
import image3 from "../images/Carrusel3.webp";
import image4 from "../images/Carrusel4.webp";
import image5 from "../images/Carrusel5.webp";
import image6 from "../images/Carrusel6.webp";

const features = [
  { id: "channel", label: "Gestor de Canales" },
  { id: "booking", label: "Motor de Reservas" },
  { id: "insights", label: "Estadísticas y Reportes" },
  { id: "payments", label: "Pagos Integrados" },
  { id: "reservations", label: "Gestión de Reservas" },
  { id: "multi", label: "Multi Propiedad" },
];

const featureDetails = {
  channel: {
    title: "Gestor de Canales",
    description:
      "Publicá tus habitaciones en Booking, Airbnb y más, y mantenelas actualizadas automáticamente. Sin dobles reservas, sin horas perdidas.",
    image: image1,
  },
  booking: {
    title: "Motor de Reservas",
    description:
      "Que los huéspedes te reserven directo desde tu web en segundos. Sin comisiones y con confirmación automática por WhatsApp o email.",
    image: image2,
  },
  insights: {
    title: "Estadísticas y Reportes",
    description:
      "Sabé en todo momento cuántas reservas tenés, cuánto ganaste y qué fechas se llenan más. Decidí con datos, no con suposiciones.",
    image: image3,
  },
  payments: {
    title: "Pagos Integrados",
    description:
      "Cobrales a tus huéspedes en un clic con links o QR. Todo queda registrado y conciliado, sin cálculos manuales ni errores.",
    image: image5,
  },
  reservations: {
    title: "Gestión de Reservas",
    description:
      "Mirá todas tus reservas en un único calendario fácil de entender. Directas o de OTAs, todas en el mismo lugar.",
    image: image4,
  },
  multi: {
    title: "Multi Propiedad",
    description:
      "Si manejás más de un alojamiento, controlalos a todos desde un solo panel. Ahorrá tiempo y mantené todo bajo control.",
    image: image6,
  },
};

export default function IncludedFeatures() {
  const [selected, setSelected] = useState("insights");
  const [imgLoaded, setImgLoaded] = useState(false);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const tabsRef = useRef(null);

  const current = useMemo(() => featureDetails[selected], [selected]);

  // Navegación con teclado (izq/der) para accesibilidad
  const handleKeyDown = (e) => {
    const idx = features.findIndex((f) => f.id === selected);
    if (e.key === "ArrowRight") {
      const next = features[(idx + 1) % features.length].id;
      setSelected(next);
    }
    if (e.key === "ArrowLeft") {
      const prev = features[(idx - 1 + features.length) % features.length].id;
      setSelected(prev);
    }
  };

  // ✅ Preload: imagen actual + prev/next para evitar “parpadeos”
  useEffect(() => {
    setImgLoaded(false);

    const idx = features.findIndex((f) => f.id === selected);
    const idsToPreload = [
      selected,
      features[(idx + 1) % features.length]?.id,
      features[(idx - 1 + features.length) % features.length]?.id,
    ].filter(Boolean);

    idsToPreload.forEach((id) => {
      const src = featureDetails[id]?.image;
      if (!src) return;
      const img = new Image();
      img.src = src;
    });
  }, [selected]);

  return (
    // ✅ Section full width con gradiente blanco → violeta
    <Box
      sx={{
        width: "100%",
        py: { xs: 6, md: 8 },
        background:
          "linear-gradient(180deg, #ffffff 0%, #efe7ff 40%, #903AF2 100%)",
      }}
    >
      {/* Contenedor */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          textAlign: "center",
          maxWidth: 1200,
          mx: "auto",
        }}
      >
        {/* Tabs / Botonera */}
        <Stack
          ref={tabsRef}
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          role="tablist"
          aria-label="Características de Ubika"
          onKeyDown={handleKeyDown}
          sx={{
            mt: 1,
            p: 1,
            borderRadius: 999,
            background:
              "linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(187, 170, 255, 0.92) 100%)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
            border: "1px solid rgba(255,255,255,0.65)",
          }}
        >
          {features.map((feat) => {
            const active = selected === feat.id;
            return (
              <Button
                key={feat.id}
                role="tab"
                aria-selected={active}
                onClick={() => setSelected(feat.id)}
                sx={{
                  position: "relative",
                  textTransform: "none",
                  px: { xs: 1.5, md: 2.2 },
                  py: 1,
                  borderRadius: 999,
                  fontWeight: 800,
                  fontFamily: "'Merriweather Sans', sans-serif",
                  fontSize: { xs: "0.82rem", sm: "0.9rem" },
                  color: active ? "#fff" : "rgba(0,0,0,0.78)",
                  backgroundColor: active ? "#903AF2" : "transparent",
                  boxShadow: active
                    ? "0 10px 20px rgba(144,58,242,0.35)"
                    : "none",
                  transition: "all .2s ease",
                  "&:hover": {
                    backgroundColor: active
                      ? "#7b2fe0"
                      : "rgba(255,255,255,0.70)",
                  },
                  "&:focus-visible": {
                    outline: "2px solid #FFE735",
                    outlineOffset: "2px",
                  },
                }}
              >
                {feat.label}
              </Button>
            );
          })}
        </Stack>

        {/* Card de contenido */}
        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <Paper
            elevation={0}
            sx={{
              mx: "auto",
              p: { xs: 3, md: 5 },
              borderRadius: { xs: 3, md: 4 },
              backgroundColor: "#fff",
              position: "relative",
              boxShadow: "0 18px 50px rgba(144,58,242,0.22)", // mejor que glow blanco duro
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
              {/* Texto */}
              <Grid item xs={12} md={6}>
                <Slide direction="up" triggerOnce key={selected}>
                  <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                    <Typography
                      fontWeight={900}
                      gutterBottom
                      sx={{
                        fontFamily: "'Red Hat Display', sans-serif",
                        fontSize: { xs: "1.7rem", md: "2.15rem" },
                        lineHeight: 1.12,
                        color: "#1d1d1f",
                        letterSpacing: -0.3,
                      }}
                    >
                      {current.title}
                    </Typography>

                    <Fade triggerOnce key={`${selected}-desc`}>
                      <Typography
                        sx={{
                          fontFamily: "'Merriweather Sans', sans-serif",
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          color: "rgba(0,0,0,0.72)",
                        }}
                      >
                        {current.description}
                      </Typography>
                    </Fade>

                    {/* Mini bullets de apoyo */}
                    <Fade triggerOnce delay={100}>
                      <Stack
                        direction="row"
                        spacing={2}
                        mt={2.5}
                        flexWrap="wrap"
                        useFlexGap
                        justifyContent={{ xs: "center", md: "flex-start" }}
                      >
                        <ChipLike>Sin solapamientos</ChipLike>
                        <ChipLike>Soporte en español</ChipLike>
                        <ChipLike>Implementación rápida</ChipLike>
                      </Stack>
                    </Fade>
                  </Box>
                </Slide>
              </Grid>

              {/* Imagen */}
              <Grid item xs={12} md={6}>
                <Fade triggerOnce key={`${selected}-img`}>
                  {/* ✅ Contenedor con aspect-ratio: no hay saltos de layout */}
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: isMdUp ? 520 : 420,
                      mx: { xs: "auto", md: 0 },
                      borderRadius: 2.5,
                      overflow: "hidden",
                      position: "relative",
                      // Reservar espacio estable (16:10 queda bien para screenshots)
                      aspectRatio: "16 / 10",
                      background:
                        "linear-gradient(135deg, rgba(144,58,242,0.10), rgba(238,150,75,0.10))",
                      border: "1px solid rgba(0,0,0,0.06)",
                      boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                    }}
                  >
                    {/* Skeleton mientras carga */}
                    {!imgLoaded && (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          transform: "none",
                        }}
                      />
                    )}

                    <Box
                      component="img"
                      src={current.image}
                      alt={current.title}
                      loading="eager"
                      decoding="async"
                      onLoad={() => setImgLoaded(true)}
                      onError={() => setImgLoaded(true)} // evita quedar “eterno” si falla
                      sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: imgLoaded ? 1 : 0,
                        transform: imgLoaded
                          ? "translateY(0)"
                          : "translateY(6px)",
                        transition:
                          "opacity .25s ease, transform .25s ease, filter .25s ease",
                        filter: imgLoaded ? "none" : "blur(6px)",
                        "&:hover": {
                          transform: "translateY(-4px)",
                        },
                      }}
                    />
                  </Box>
                </Fade>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

/** Mini “Chip” estilizado para bullets */
function ChipLike({ children }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.65,
        borderRadius: 999,
        fontSize: { xs: "0.78rem", md: "0.82rem" },
        fontWeight: 800,
        color: "#903AF2",
        backgroundColor: "rgba(144,58,242,0.08)",
        border: "1px solid rgba(144,58,242,0.22)",
      }}
    >
      {children}
    </Box>
  );
}
